import React from 'react';
import { Shield, Lock, Eye, Download, AlertTriangle, CheckCircle } from 'lucide-react';

export const SecurityDashboard: React.FC = () => {
  const securityMetrics = {
    dataEncryption: 'AES-256',
    lastSecurityAudit: '2024-01-10',
    mfaEnabled: true,
    dataBackups: 'Daily',
    accessLogs: 247,
    suspiciousActivity: 0,
  };

  const complianceChecks = [
    { name: 'HIPAA Compliance*', status: 'pending', description: 'All PHI properly encrypted and secured (certification pending)' },
    { name: 'SOC 2 Type II*', status: 'pending', description: 'Security controls independently verified (certification pending)' },
    { name: 'Data Retention Policy', status: 'compliant', description: 'Automatic data purging per policy' },
    { name: 'Access Controls', status: 'compliant', description: 'Role-based permissions enforced' },
    { name: 'Audit Logging', status: 'compliant', description: 'All actions logged and monitored' },
    { name: 'Backup Encryption', status: 'compliant', description: 'All backups encrypted at rest' },
  ];

  const getStatusIcon = (status: string) => {
    if (status === 'pending') return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    return <CheckCircle className="h-5 w-5 text-green-500" />;
  };

  const getStatusColor = (status: string) => {
    if (status === 'pending') return 'border-yellow-200 bg-yellow-50';
    return 'border-green-200 bg-green-50';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">Security & Compliance</h2>
          <p className="text-slate-600 mt-1">Your data security and privacy status</p>
        </div>
        <div className="flex items-center space-x-2 px-4 py-2 bg-yellow-50 text-yellow-700 rounded-lg">
          <Shield className="h-5 w-5" />
          <span className="font-medium">HIPAA Compliance Pending*</span>
        </div>
      </div>

      {/* Security Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <Lock className="h-8 w-8 text-blue-500" />
            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Active</span>
          </div>
          <h3 className="text-lg font-semibold text-slate-800">Data Encryption</h3>
          <p className="text-2xl font-bold text-blue-600">{securityMetrics.dataEncryption}</p>
          <p className="text-sm text-slate-600">End-to-end encryption</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <Eye className="h-8 w-8 text-purple-500" />
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Monitored</span>
          </div>
          <h3 className="text-lg font-semibold text-slate-800">Access Logs</h3>
          <p className="text-2xl font-bold text-purple-600">{securityMetrics.accessLogs}</p>
          <p className="text-sm text-slate-600">This month</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <AlertTriangle className="h-8 w-8 text-green-500" />
            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Secure</span>
          </div>
          <h3 className="text-lg font-semibold text-slate-800">Threats Detected</h3>
          <p className="text-2xl font-bold text-green-600">{securityMetrics.suspiciousActivity}</p>
          <p className="text-sm text-slate-600">This month</p>
        </div>
      </div>

      {/* Compliance Status */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">Compliance Status</h3>
            <p className="text-sm text-slate-500">*Certifications pending</p>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {complianceChecks.map((check, index) => (
              <div key={index} className={`flex items-start space-x-3 p-4 border rounded-lg ${getStatusColor(check.status)}`}>
                {getStatusIcon(check.status)}
                <div>
                  <h4 className="font-medium text-slate-800">{check.name}</h4>
                  <p className="text-sm text-slate-600">{check.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Privacy Controls */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Privacy Controls</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
            <div>
              <h4 className="font-medium text-slate-800">Data Sharing with Providers</h4>
              <p className="text-sm text-slate-600">Allow healthcare providers to access your data</p>
            </div>
            <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
            <div>
              <h4 className="font-medium text-slate-800">Anonymous Analytics</h4>
              <p className="text-sm text-slate-600">Help improve the platform with anonymous usage data</p>
            </div>
            <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600">
              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-6" />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
            <div>
              <h4 className="font-medium text-slate-800">Research Participation</h4>
              <p className="text-sm text-slate-600">Allow anonymized data to be used for medical research</p>
            </div>
            <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-slate-300">
              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition translate-x-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Data Rights */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Your Data Rights</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button className="flex items-center space-x-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              <Download className="h-5 w-5 text-blue-500" />
              <span className="font-medium text-slate-800">Download My Data</span>
            </button>
            <button className="flex items-center space-x-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              <Eye className="h-5 w-5 text-green-500" />
              <span className="font-medium text-slate-800">View Access Log</span>
            </button>
            <button className="flex items-center space-x-3 p-4 border border-red-200 rounded-lg hover:bg-red-50 transition-colors text-red-600">
              <AlertTriangle className="h-5 w-5" />
              <span className="font-medium">Delete My Account</span>
            </button>
          </div>
        </div>
      </div>

      {/* Compliance Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-6 w-6 text-yellow-600 mt-1" />
          <div>
            <h4 className="font-semibold text-yellow-800 mb-2">Compliance Status Notice</h4>
            <p className="text-sm text-yellow-700 leading-relaxed">
              We are actively pursuing HIPAA compliance and SOC 2 certification. Our platform implements enterprise-grade 
              security measures including AES-256 encryption, secure access controls, and comprehensive audit logging. 
              We expect to complete our compliance certifications within the next 90 days.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
